import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';
import Icon from '~/components/Icon/Icon';
import { MODAL_EDIT_ADDRESS_PARAM, useModal } from '~/hooks/useModal';
import { userStore } from '~/stores';
import { DeliveryAddress } from '~/stores/UserStore';
import { getAddressModalParams } from '~/utils/addressModal';

import styles from './MobileHeaderAddress.module.scss';

type Props = {
  handleDeliveryAddress: () => void;
  contentText: string;
  headerColor?: boolean;
};

const MobileHeaderAddressCD = observer(
  ({ handleDeliveryAddress, contentText }: Props) => (
    <div
      className={styles.container}
      onClick={handleDeliveryAddress}
      data-company={company.name}
    >
      <span className={styles.contentText}>{contentText}</span>
      <Icon type="chevron" size={16} className={styles.icon} />
    </div>
  ),
);

const MobileHeaderAddressLocalee = observer(
  ({ handleDeliveryAddress, contentText, headerColor }: Props) => (
    <div
      data-button="mobile-header-address"
      className={classes([styles.container, headerColor && styles.headerColor])}
      onClick={handleDeliveryAddress}
      data-company={company.name}
    >
      {company.showLocationIconMobileCabinetPage && (
        <Icon
          type={company.match({
            circlek: 'location-fill-pin',
            default: 'location',
          })}
          size={20}
          className={styles.iconLocation}
        />
      )}
      <span className={styles.contentText}>{contentText}</span>
      <Icon type="chevron" size={16} className={styles.icon} />
    </div>
  ),
);

const getHeader = (companyName: CompanyName) => {
  switch (companyName) {
    case CompanyName.CityDrinks:
      return MobileHeaderAddressCD;
    default:
      return MobileHeaderAddressLocalee;
  }
};

const formatDeliveryAddress = (address: DeliveryAddress) =>
  `${address.address1} ${address.address2}`;

const MobileHeaderAddress = ({ headerColor }: { headerColor?: boolean }) => {
  const { t } = useTranslation();
  const { openModal } = useModal();

  const handleDeliveryAddress = () => {
    const [targetModal, source, params] = getAddressModalParams(
      'MobileHeaderAddress',
      {
        [MODAL_EDIT_ADDRESS_PARAM]: userStore?.deliveryAddress?.addressId || '',
      },
    );

    openModal(targetModal, source, params);
  };

  const contentText = userStore.deliveryAddress
    ? formatDeliveryAddress(userStore.deliveryAddress)
    : t('selectAddressPlaceholder');

  const Component = getHeader(company.name);

  return (
    <Component
      handleDeliveryAddress={handleDeliveryAddress}
      contentText={contentText}
      headerColor={headerColor}
    />
  );
};

export default observer(MobileHeaderAddress);
